import { useEffect } from 'react';
import { Link, NavLink, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import valinorLogo from './components/ui/Logo_design_for_VALI-removebg-preview(1).png';
import { HomePage } from './pages/HomePage';
import { MissionPage } from './pages/MissionPage';
import { ImpactPage } from './pages/ImpactPage';
import { ScoutPage } from './pages/ScoutPage';
import { ProjectsPage } from './pages/ProjectsPage';
import { ProjectScoutPage } from './pages/ProjectScoutPage';
import { ConnectPage } from './pages/ConnectPage';

function Header() {
  return (
    <header className="fixed inset-x-0 top-0 z-30 flex w-full flex-wrap items-center justify-center gap-3 border-b border-white/15 bg-black/85 px-5 py-3 text-white shadow-[0_1px_18px_rgba(0,0,0,0.35)] backdrop-blur-md md:flex-nowrap md:justify-between md:px-8">
      <Link
        to="/"
        className="flex w-full items-center justify-center gap-3 md:w-auto"
        aria-label="Valinor Dynamics home"
      >
        <img
          src={valinorLogo}
          alt="Valinor Dynamics logo"
          className="h-12 w-auto md:h-14"
        />
        <span className="text-sm font-extrabold tracking-[0.2em] md:text-base">
          VALINOR DYNAMICS
        </span>
      </Link>
      <nav className="flex items-center gap-4 text-[0.68rem] uppercase tracking-[0.12em]">
        <NavLink
          to="/"
          end
          className={({ isActive }) =>
            `transition hover:text-white ${isActive ? 'text-white' : 'text-white'}`
          }
        >
          Home
        </NavLink>
        <NavLink
          to="/mission"
          className={({ isActive }) =>
            `transition hover:text-white ${isActive ? 'text-white' : 'text-white'}`
          }
        >
          Mission
        </NavLink>
        <NavLink
          to="/projects"
          className={({ isActive }) =>
            `transition hover:text-white ${isActive ? 'text-white' : 'text-white'}`
          }
        >
          Projects
        </NavLink>
        <NavLink
          to="/impact"
          className={({ isActive }) =>
            `transition hover:text-white ${isActive ? 'text-white' : 'text-white'}`
          }
        >
          Impact
        </NavLink>
        <NavLink
          to="/connect"
          className={({ isActive }) =>
            `transition hover:text-white ${isActive ? 'text-white' : 'text-white'}`
          }
        >
          Connect
        </NavLink>
      </nav>
    </header>
  );
}

function ScrollToHash() {
  const { hash, pathname } = useLocation();

  useEffect(() => {
    if (hash) {
      const element = document.getElementById(decodeURIComponent(hash.slice(1)));
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
      return;
    }
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, [hash, pathname]);

  return null;
}

function Footer() {
  return (
    <footer className="border-t border-white/10 bg-black px-4 pb-8 pt-10 text-white md:px-8">
      <div className="mx-auto w-full max-w-7xl">
        <div className="border-b border-white/10 pb-8">
          <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
            <div className="space-y-3">
              <p className="text-sm font-semibold text-white">Valinor Dynamics</p>
              <ul className="space-y-2 text-sm text-white">
                <li>Dual-tech defense company</li>
                <li>Autonomous ground vehicle systems</li>
                <li>Camera and sensor platforms</li>
              </ul>
            </div>

            <div className="space-y-3">
              <p className="text-sm font-semibold text-white">Capabilities</p>
              <ul className="space-y-2 text-sm text-white">
                <li>Ground mobility</li>
                <li>Vision and tracking</li>
                <li>Mission awareness</li>
              </ul>
            </div>

            <div className="space-y-3">
              <p className="text-sm font-semibold text-white">Applications</p>
              <ul className="space-y-2 text-sm text-white">
                <li>Reconnaissance missions</li>
                <li>Perimeter and site security</li>
                <li>Integrated defense operations</li>
              </ul>
            </div>

            <div className="space-y-3">
              <p className="text-sm font-semibold text-white">Legal</p>
              <ul className="space-y-2 text-sm text-white">
                <li id="terms-of-service">
                  Materials on this site are provided for general information about Valinor
                  Dynamics and its work.
                </li>
                <li id="license">
                  All branding, visuals, and written content remain the property of Valinor
                  Dynamics unless otherwise stated.
                </li>
              </ul>
            </div>
          </div>
        </div>

        <div
          id="connect"
          className="flex flex-col gap-4 pt-5 text-sm text-neutral-500 md:flex-row md:items-center md:justify-between"
        >
          <p>Copyright 2026 Valinor Dynamics. All rights reserved.</p>
          <nav className="flex flex-wrap items-center gap-3">
            <a href="#terms-of-service" className="transition hover:text-white">
              Terms of Service
            </a>
            <span>|</span>
            <a href="#license" className="transition hover:text-white">
              License
            </a>
          </nav>
        </div>
      </div>
    </footer>
  );
}

function App() {
  return (
    <div className="min-h-screen bg-neutral-950 text-neutral-100">
      <ScrollToHash />
      <Header />
      <main>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/mission" element={<MissionPage />} />
          <Route path="/impact" element={<ImpactPage />} />
          <Route path="/scout" element={<ScoutPage />} />
          <Route path="/projects" element={<ProjectsPage />} />
          <Route path="/projects/scout" element={<ProjectScoutPage />} />
          <Route path="/connect" element={<ConnectPage />} />
          <Route path="/capabilities" element={<Navigate to="/projects" replace />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
      <Footer />
    </div>
  );
}

export default App;
